import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  CircularProgress,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getContacts,
  createContact,
  updateContact,
  deleteContact,
  exportContactsCsv,
  importContactsCsv,
  Contact,
} from '../api/contacts';

const emptyContact: Partial<Contact> = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  organization: '',
  notes: '',
};

export default function ContactsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [form, setForm] = useState<Partial<Contact>>(emptyContact);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['contacts', search],
    queryFn: () => getContacts(search ? { search } : {}),
  });

  const createMutation = useMutation({
    mutationFn: createContact,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['contacts'] }); handleClose(); },
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Contact> }) => updateContact(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['contacts'] }); handleClose(); },
  });
  const deleteMutation = useMutation({
    mutationFn: deleteContact,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts'] }),
  });
  const importMutation = useMutation({
    mutationFn: importContactsCsv,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts'] }),
  });

  const handleOpen = (contact?: Contact) => {
    setEditing(contact || null);
    setForm(contact ? { ...contact } : emptyContact);
    setOpen(true);
  };
  const handleClose = () => { setOpen(false); setEditing(null); };

  const handleSave = () => {
    if (editing) updateMutation.mutate({ id: editing.id, data: form });
    else createMutation.mutate(form as Omit<Contact, 'id'>);
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) importMutation.mutate(file);
    e.target.value = '';
  };

  const contacts = data?.results || [];

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
        <Typography variant="h6" fontWeight={600} sx={{ flexGrow: 1 }}>
          Contacts
        </Typography>
        <TextField
          size="small"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
          inputProps={{ 'aria-label': 'Search contacts' }}
        />
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => exportContactsCsv()}>
          Export CSV
        </Button>
        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={importMutation.isPending}
        >
          Import CSV
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={handleImport}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          New
        </Button>
      </Stack>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary">No contacts.</Typography>
                </TableCell>
              </TableRow>
            )}
            {contacts.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell>{`${c.first_name} ${c.last_name}`.trim() || '—'}</TableCell>
                <TableCell>{c.email}</TableCell>
                <TableCell>{c.phone || '—'}</TableCell>
                <TableCell>{c.organization || '—'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpen(c)} aria-label="Edit contact">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => deleteMutation.mutate(c.id)} aria-label="Delete contact">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Contact' : 'New Contact'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {(['first_name', 'last_name', 'email', 'phone', 'organization', 'notes'] as const).map(
              (field) => (
                <TextField
                  key={field}
                  label={field.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  fullWidth
                  value={(form as any)[field] || ''}
                  onChange={(e) => setForm((p) => ({ ...p, [field]: e.target.value }))}
                  multiline={field === 'notes'}
                  minRows={field === 'notes' ? 3 : undefined}
                />
              )
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
